import * as jsPdf from 'jspdf';
import * as moment from 'moment-timezone';
import { ITest } from "src/Interfaces/TestInterface";
import { IWell } from "src/Interfaces/WellInterface";
import { fillEmptyRows } from '../MudReport/PopDataMud';
import { textArrayInBoxes, textLeftJustifiedInBox } from '../PopulateDataMethods';
import { drawHeader } from "./DrawReportVolume";
import * as MM from './SpecMilsExternal';

interface IStorageTankInfo {
    number: string | number;
    storageType: string;
    fluidType: string;
    fluidSubType: string;
    capacity: number;
}
export const createExternalReport = (tests: ITest[], well: IWell, logoBlob: HTMLImageElement, externalReport: jsPdf) => {
    const sortedTests = tests.sort((a, b) => a.testDate - b.testDate);
    popExternalReport(sortedTests, well, logoBlob, externalReport);
};

const popExternalReport = (tests: ITest[], well: IWell, logoBlob: HTMLImageElement, externalReport: jsPdf) => {
    drawHeader(tests[tests.length - 1], well, logoBlob, externalReport);
    const externalList = getExternalStorageTankList(tests);
    popExternalRowHeaders(externalReport);
    popExternalHeaders(externalList, externalReport);
    let i = popDataColumns(externalList, tests, well, 0, externalReport);
    while (i < tests.length) {
        externalReport.addPage();
        drawHeader(tests[tests.length - 1], well, logoBlob, externalReport);
        popExternalRowHeaders(externalReport);
        popExternalHeaders(externalList, externalReport);
        i = popDataColumns(externalList, tests, well, i, externalReport);
    }
};

const getExternalStorageTankList = (tests: ITest[]) => {
    const retArr: IStorageTankInfo[] = [];

    tests.forEach(test => {
        test.externalStorage.forEach(tank => {
            if (!retArr.some(tankInfo => tankInfo.number === tank.number && tankInfo.storageType === tank.storageType && tankInfo.fluidSubType === tank.fluidSubType && tankInfo.capacity === tank.capacity)) {
                retArr.push({number: tank.number, storageType: tank.storageType, fluidType: tank.fluidType, fluidSubType: tank.fluidSubType, capacity: tank.capacity});
            }
        });
    });
    return retArr.sort((a, b) => Number(a.number) - Number(b.number));
};

const popExternalRowHeaders = (externalReport: jsPdf) => {
    const bigBoldHeaders = [
        ['Fluid Type'],
        ['Test Date'],
        [undefined],
    ];
    externalReport.setFontStyle('bold');
    textArrayInBoxes(bigBoldHeaders, MM.ROW_HEADERS_BIG_LEFT, MM.ROW_HEADERS_BIG_TOP, MM.ROW_HEADERS_BIG_WIDTH, MM.ROW_HEADERS_BIG_HEIGHT, 'left', externalReport);
    
};

const popExternalHeaders = (storageList: IStorageTankInfo[], externalReport: jsPdf) => {
    const headerText = 'External Storage (bbls)';
    const storageText: any = [
        [undefined]
    ];

    const storageHeader = [
        [ 
            { cols: 1, text: 'Number' },
            { cols: 2, text: 'Storage Type'},
            { cols: 2, text: 'Fluid Type'},
            { cols: 2, text: 'Fluid Subtype'},
            { cols: 2, text: 'Capacity'}
        ]
    ];

    storageList.forEach(value => storageText.push(
        [
            { cols: 1, text: value.number },
            { cols: 2, text: value.storageType },
            { cols: 2, text: value.fluidType },
            { cols: 2, text: value.fluidSubType },
            { cols: 2, text: value.capacity }
        ]
    ));

    textLeftJustifiedInBox(headerText, MM.EXTERNAL_STORAGE_LEFT, MM.EXTERNAL_STORAGE_TOP, MM.EXTERNAL_STORAGE_WIDTH, MM.ROW_SECTION_HEADER_HEIGHT, externalReport);
    externalReport.setFontSize(MM.HEADER_2_SIZE);
    fillEmptyRows(storageHeader, MM.EXTERNAL_STORAGE_ROWS);
    textArrayInBoxes(storageHeader, MM.EXTERNAL_STORAGE_LEFT + MM.INDENT_WIDTH, MM.EXTERNAL_STORAGE_TOP + MM.ROW_SECTION_HEADER_HEIGHT, MM.EXTERNAL_STORAGE_WIDTH, MM.EXTERNAL_STORAGE_HEIGHT - MM.ROW_SECTION_HEADER_HEIGHT, 'left', externalReport);
    externalReport.setFontStyle('normal');
    fillEmptyRows(storageText, MM.EXTERNAL_STORAGE_ROWS);
    textArrayInBoxes(storageText, MM.EXTERNAL_STORAGE_LEFT + MM.INDENT_WIDTH, MM.EXTERNAL_STORAGE_TOP + MM.ROW_SECTION_HEADER_HEIGHT, MM.EXTERNAL_STORAGE_WIDTH, MM.EXTERNAL_STORAGE_HEIGHT - MM.ROW_SECTION_HEADER_HEIGHT, 'left', externalReport);
    drawColLines(MM.EXTERNAL_STORAGE_LEFT, MM.EXTERNAL_STORAGE_WIDTH + 0.5, externalReport);
};

const popDataColumns = (storageList: IStorageTankInfo[], tests: ITest[], well: IWell, startIndex: number, externalReport: jsPdf) => {
    let colNum = 0;
    let i;
    for (i = startIndex; i < tests.length; ++i) {
        if (tests[i].externalStorage.length === 0) {
            continue;
        }
        if( MM.COLUMN_START_LEFT + MM.COLUMN_WIDTH * colNum < MM.FULL_WIDTH ) {
            popSingleColumn(storageList, tests[i], well, colNum++, externalReport);
        } else {
            return i;
        }
    }

    return i;
};

const popSingleColumn = (storageList: IStorageTankInfo[], test: ITest, well:IWell, colNum:number, externalReport:jsPdf) => {
    const tempLeft = MM.COLUMN_START_LEFT + MM.COLUMN_WIDTH * colNum + 0.5;
    const bigBoldHeaders = [
        [test.propertyType],
        [moment.unix(test.testDate).tz(well.timeZoneName).format('M/D/YYYY')],
        [moment.unix(test.testDate).tz(well.timeZoneName).format('h:mm a')],
    ];

    externalReport.setFontStyle('bold');
    textArrayInBoxes(bigBoldHeaders, tempLeft, MM.ROW_HEADERS_BIG_TOP, MM.COLUMN_WIDTH, MM.ROW_HEADERS_BIG_HEIGHT, 'left', externalReport);
    externalReport.setFontStyle('normal');

    const normalData: any[] = [];
    fillEmptyRows(normalData, MM.EXTERNAL_STORAGE_ROWS);
    test.externalStorage.forEach(externalStorage => {
        if (externalStorage.inUse) {
            const dataIndex = storageList.findIndex(item => 
                item.number === externalStorage.number
                && item.storageType === externalStorage.storageType
                && item.fluidType === externalStorage.fluidType
                && item.fluidSubType === externalStorage.fluidSubType
                && item.capacity === externalStorage.capacity
            );
            if (dataIndex >= 0) {
                normalData[dataIndex + 1] = [externalStorage.todayFluidAmount];
            }
        }
    });
    textArrayInBoxes(normalData, tempLeft, MM.EXTERNAL_STORAGE_TOP + MM.ROW_SECTION_HEADER_HEIGHT, MM.COLUMN_WIDTH, MM.EXTERNAL_STORAGE_HEIGHT, 'right', externalReport);
    drawColLines(tempLeft, MM.COLUMN_WIDTH, externalReport);
};

const drawColLines = (left: number, width:number, externalReport: jsPdf) => {
    externalReport.setLineWidth(MM.THIN_LINE);
    externalReport.line(
        left,
        MM.EXTERNAL_STORAGE_TOP + MM.ROW_SECTION_HEADER_HEIGHT,
        left + width,
        MM.EXTERNAL_STORAGE_TOP + MM.ROW_SECTION_HEADER_HEIGHT
    );

    externalReport.line(
        left,
        MM.EXTERNAL_STORAGE_TOP + MM.ROW_SECTION_HEADER_HEIGHT + (MM.EXTERNAL_STORAGE_HEIGHT / MM.EXTERNAL_STORAGE_ROWS) - MM.LINE_Y_OFFSET,
        left + width,
        MM.EXTERNAL_STORAGE_TOP + MM.ROW_SECTION_HEADER_HEIGHT + (MM.EXTERNAL_STORAGE_HEIGHT / MM.EXTERNAL_STORAGE_ROWS) - MM.LINE_Y_OFFSET
    );
};


// WEBPACK FOOTER //
// ./src/Services/Pdf/VolumeAccountingReport/DrawReportExternal.ts